import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast as showToast } from 'sonner'
import { type Toast } from '#app/utils/toast.server.ts'

// Map of toast message keys to translation keys
const toastTranslationMap: Record<string, { title: string; description: string }> = {
	'passwordChanged': {
		title: 'errors:success.passwordChanged',
		description: 'errors:success.passwordChangedDesc',
	},
	'passwordReset': {
		title: 'errors:success.passwordReset',
		description: 'errors:success.passwordResetDesc',
	},
	'twoFactorEnabled': {
		title: 'errors:success.twoFactorEnabled',
		description: 'errors:success.twoFactorEnabledDesc',
	},
	'twoFactorDisabled': {
		title: 'errors:success.twoFactorDisabled',
		description: 'errors:success.twoFactorDisabledDesc',
	},
	'photoUploaded': {
		title: 'errors:success.photoUploaded',
		description: 'errors:success.photoUploadedDesc',
	},
	'profileUpdated': {
		title: 'errors:success.updated',
		description: 'errors:success.profileUpdatedDesc',
	},
}

export function useToast(toast?: Toast | null) {
	const { t } = useTranslation(['errors'])
	
	useEffect(() => {
		if (toast) {
			setTimeout(() => {
				// Check if this is a translation key or a direct message
				let title = toast.title
				let description = toast.description
				
				// If title starts with 'toast:', it's a translation key
				if (toast.title?.startsWith('toast:')) {
					const key = toast.title.replace('toast:', '')
					const translation = toastTranslationMap[key]
					if (translation) {
						title = t(translation.title)
						description = t(translation.description)
					}
				} else {
					// Check for known titles to translate
					const knownTitles: Record<string, string> = {
						'Password Changed': 'errors:success.passwordChanged',
						'Password reset successful': 'errors:success.passwordReset',
						'Two-factor authentication enabled successfully': 'errors:success.twoFactorEnabled',
						'Two-factor authentication disabled successfully': 'errors:success.twoFactorDisabled',
						'Photo uploaded successfully': 'errors:success.photoUploaded',
					}
					
					const knownDescriptions: Record<string, string> = {
						'Your password has been changed.': 'errors:success.passwordChangedDesc',
						'Your password has been reset.': 'errors:success.passwordResetDesc',
					}
					
					if (title && knownTitles[title]) {
						const translationKey = knownTitles[title]
						if (translationKey) {
							title = t(translationKey)
						}
					}
					
					if (description && knownDescriptions[description]) {
						const translationKey = knownDescriptions[description]
						if (translationKey) {
							description = t(translationKey)
						}
					}
				}
				
				// Handle provider connection messages with interpolation
				if (description.includes('{{provider}}')) {
					// Extract provider name from original description
					const match = description.match(/(\w+) connected successfully/)
					if (match) {
						description = t('errors:success.providerConnected', { provider: match[1] })
					}
				}
				
				showToast[toast.type](title, {
					id: toast.id,
					description: description,
				})
			}, 0)
		}
	}, [toast, t])
}
